import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';

import { catchError, Observable, throwError } from 'rxjs';

import { ConfirmDialogService } from '@app/core/services/confirm-dialog.service';
import {
  EStorageKey,
} from '@app/core/api/auth/auth.enum';
import { parseError } from '@shared/helpers';
import { SpinnerService } from '../services/spinner.service';
import { StorageService } from '../services/storage.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  private readonly ALLOWED_URLS: string[] = [
    'login',
    'register',
    'password-forgot',
    'password-change',
    'email-validate',
  ];

  constructor(
    private router: Router,
    private confirm: ConfirmDialogService,
    private spinner: SpinnerService,
    private storage: StorageService,
    ) {
  }

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      this.isRequestRequiredToken(request) &&
      !request.headers.get('Authorization')
    ) {
      const authToken = this.storage.get(EStorageKey.AuthToken);
      if (!authToken && this.isPublicProjectPreviewRequest(request)) {
        return next.handle(request);
      }
      const token = authToken;
      const setHeaders: { Authorization: string; AuthorizationAdmin?: string } = {
        Authorization: `Bearer ${token}`,
      };

      // eslint-disable-next-line no-param-reassign
      request = request.clone({
        params: request.params.delete('useAuthToken'),
        setHeaders,
      });
    }

    return next.handle(request)
      .pipe(
        catchError((error) => {
            if (error instanceof HttpErrorResponse) {
              this.handleError(request, error);
            }

            return throwError(error);
          },
        ),
      );
  }

  private handleError(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    switch (true) {

      case (error.status === 401):
        this.storage.remove(EStorageKey.AuthToken);
        break;

      case (error.status === 403):
        this.spinner.hide();
        this.confirm.error(parseError(error));
        break;

      case (error.status === 404):
        if (this.isPublicProjectPreviewRequest(request)) {
          this.spinner.hide();
          break;
        }
        this.router.navigate(['/not-found']);
        this.spinner.hide();
        this.confirm.error(parseError(error));
        break;

      case (error.status === 422):
        //TODO: This error must be refactored to allow multiple errors (array of errors)
        this.confirm.error('There was an error while processing your request: ' + parseError(error));
        this.spinner.hide();
        break;

      case (request.method.toLowerCase() === 'delete' || error.status >= 500):
        this.confirm.error(parseError(error));
        break;

      default:
        break;
    }
  }

  private isRequestRequiredToken(request: HttpRequest<unknown>): boolean {
    return !!request &&
      !!request.url &&
      request.url.split('.').pop() !== 'svg' &&
      this.ALLOWED_URLS.indexOf(request.url.split('/').pop() || '') === -1;
  }

  /** Unauthenticated GET /api/public/project/:share_url — no Bearer; 404 handled in-page. */
  private isPublicProjectPreviewRequest(request: HttpRequest<unknown>): boolean {
    return (
      request.method === 'GET' &&
      !!request.url &&
      request.url.includes('/api/public/project/')
    );
  }

}
